import React, { useCallback, useEffect, useState } from 'react';
import { Button, Checkbox, Divider, Form, Input, Popconfirm, Space, Spin, Table, Tag, Tooltip, Typography, message } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../../shared/i18n';
import tmService from '../../../services/tmService';
import { fmtTime, ok, showSecretOnce, toastErr } from './common';

const { Text } = Typography;

const EVENT_LABEL = {
    delivered: 'Delivered', bounced: 'Bounced', complained: 'Complained', opened: 'Opened', clicked: 'Clicked',
    unsubscribed: 'Unsubscribed', deferred: 'Deferred', failed: 'Failed', rejected: 'Rejected',
    virus: 'Virus', spam_rejected: 'Rejected as spam',
};

const DELIVERY = {
    pending: { color: 'processing', text: 'Pending' }, success: { color: 'green', text: 'Success' },
    failed: { color: 'gold', text: 'Retrying' }, gave_up: { color: 'red', text: 'Gave up' },
};

const Webhook = ({ stream }) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [cfg, setCfg] = useState(null);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [deliveries, setDeliveries] = useState([]);
    const [dLoading, setDLoading] = useState(false);

    const load = useCallback(async () => {
        try {
            const r = await tmService.getWebhook(stream.id);
            setCfg(r?.data || null);
            form.setFieldsValue({ url: r?.data?.url || '', events: r?.data?.events || [] });
        } catch (e) {
            toastErr(e, t('Failed to load'));
        }
    }, [stream.id, form, t]);

    const loadDeliveries = useCallback(async () => {
        setDLoading(true);
        try {
            const r = await tmService.getWebhookDeliveries(stream.id);
            setDeliveries(Array.isArray(r?.data) ? r.data : []);
        } catch (e) {
            toastErr(e, t('Failed to load'));
        } finally {
            setDLoading(false);
        }
    }, [stream.id, t]);

    useEffect(() => { load(); loadDeliveries(); }, [load, loadDeliveries]);

    const save = async (rotate = false) => {
        const v = await form.validateFields();
        setSaving(true);
        try {
            const r = await tmService.saveWebhook(stream.id, { url: v.url?.trim() || null, events: v.events, rotate_secret: rotate });
            if (ok(r)) {
                message.success(t('Saved'));
                if (r.data?.secret) {
                    showSecretOnce({ title: t('Signing secret'), secret: r.data.secret, note: t('Used to verify the X-Yeap-Signature header') });
                }
                load();
            }
        } catch (e) {
            toastErr(e, t('Failed to save'));
        } finally {
            setSaving(false);
        }
    };

    const test = async () => {
        setTesting(true);
        try {
            const r = await tmService.testWebhook(stream.id);
            if (ok(r)) message.success(t('Test succeeded'));
        } catch (e) {
            toastErr(e, t('Test failed'), true);
        } finally {
            setTesting(false);
            loadDeliveries();
        }
    };

    if (!cfg) return <div style={{ textAlign: 'center', padding: 32 }}><Spin /></div>;

    const events = cfg.available_events || Object.keys(EVENT_LABEL);
    const columns = [
        { title: t('Time'), dataIndex: 'created_at', width: 160, render: fmtTime },
        { title: t('Events'), dataIndex: 'event_type', width: 100, render: (v) => t(EVENT_LABEL[v] || v) },
        {
            title: t('Status'), dataIndex: 'status', width: 100,
            render: (v) => <Tag color={DELIVERY[v]?.color}>{t(DELIVERY[v]?.text || v)}</Tag>,
        },
        { title: 'HTTP', dataIndex: 'last_status_code', width: 70, render: (v) => v || '-' },
        { title: t('Retry'), dataIndex: 'attempts', width: 60, responsive: ['md'] },
        {
            title: t('Error'), dataIndex: 'last_error', ellipsis: true, responsive: ['md'],
            render: (v, r) => v ? <Tooltip title={v}><Text type="danger" style={{ fontSize: 12 }}>{v}</Text></Tooltip>
                : r.next_attempt_at ? <Text type="secondary" style={{ fontSize: 12 }}>{t('Next')} {fmtTime(r.next_attempt_at)}</Text> : '-',
        },
    ];

    return (
        <div>
            <Form form={form} layout="vertical" style={{ maxWidth: 640 }}>
                <Form.Item name="url" label={t('Endpoint URL')} rules={[{ type: 'url', message: t('Enter a valid URL') }]}>
                    <Input placeholder="https://example.com/webhooks/yeap" allowClear />
                </Form.Item>
                <Form.Item name="events" label={t('Events')} rules={[{ type: 'array', min: 1, message: t('Select at least one event') }]}>
                    <Checkbox.Group options={events.map((e) => ({ value: e, label: t(EVENT_LABEL[e] || e) }))} />
                </Form.Item>
                <Space wrap>
                    <Button type="primary" loading={saving} onClick={() => save(false)}>{t('Save')}</Button>
                    <Button onClick={test} loading={testing} disabled={!cfg.url}>{t('Send test')}</Button>
                    {cfg.has_secret && cfg.url && (
                        <Popconfirm title={t('The old secret stops working immediately')} onConfirm={() => save(true)}>
                            <Button>{t('Reset signing secret')}</Button>
                        </Popconfirm>
                    )}
                </Space>
            </Form>
            <Divider orientation="left" plain>
                <Space>{t('Recent deliveries')}<Button size="small" type="text" icon={<ReloadOutlined />} onClick={loadDeliveries} /></Space>
            </Divider>
            <Table rowKey="id" size="small" loading={dLoading} dataSource={deliveries} columns={columns} pagination={{ pageSize: 20 }} />
        </div>
    );
};

export default Webhook;
